// src/components/inputs/MultiImageUploader.jsx

import React, { useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { validateImageFile } from "../../utils/imageCompression";
import useDeleteMedia from "../../hooks/media/useDeleteMedia";

// ✅ Import Custom Hook
import useImageUploadQueue from "../../hooks/upload/useImageUploadQueue";

// ✅ Import Helpers
import {
    canAddMoreImages,
    getRemainingSlots,
    showMaxImagesError,
    showFilesAddedSuccess,
    showValidationError,
    showRejectedFilesError,
    showDeleteSuccess,
    showRetryMessage,
} from "../../utils/upload/imageUploadHelpers";

// ✅ Import Sub-components
import UploadDropZone from "./MultiImageUploader/UploadDropZone";
import ImageGridDisplay from "./MultiImageUploader/ImageGridDisplay";

/**
 * Multi Image Uploader Component with Drag & Drop
 */
const MultiImageUploader = ({
    value = [],
    onChange,
    maxImages = 10,
    maxSizeMB = 2,
    required = false,
    disabled = false,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const { deleteSingleMedia } = useDeleteMedia();

    // ✅ استخدام Custom Hook
    const {
        uploadQueue,
        setUploadQueue,
        uploadingImages,
        errors,
        retryUpload,
        removeFromUploadQueue,
    } = useImageUploadQueue(value, onChange, maxSizeMB, isArabic);

    // ✅ Validate and process files
    const processFiles = useCallback((files) => {
        if (!files || files.length === 0) return;

        // Check if we can add more images
        if (!canAddMoreImages(value.length, uploadQueue.length, maxImages)) {
            showMaxImagesError(maxImages, isArabic);
            return;
        }

        const remainingSlots = getRemainingSlots(value.length, uploadQueue.length, maxImages);
        const filesToAdd = Array.from(files).slice(0, remainingSlots);

        // Validate files
        const validFiles = [];
        for (const file of filesToAdd) {
            const validation = validateImageFile(file, maxSizeMB);

            if (!validation.valid) {
                showValidationError(validation, isArabic);
                continue;
            }

            validFiles.push(file);
        }

        if (validFiles.length === 0) return;

        // Add to queue with local previews
        const newQueueItems = validFiles.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            preview: URL.createObjectURL(file),
            status: 'pending'
        }));
        console.log("🚀 ~ MultiImageUploader ~ newQueueItems:", newQueueItems)
        setUploadQueue((prev) => [...prev, ...newQueueItems]);

        // Show success message
        showFilesAddedSuccess(validFiles.length, isArabic);
    }, [maxImages, value.length, uploadQueue.length, maxSizeMB, isArabic, setUploadQueue]);

    // ✅ استخدام react-dropzone
    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        // Handle rejected files
        if (rejectedFiles && rejectedFiles.length > 0) {
            showRejectedFilesError(rejectedFiles, isArabic);
        }

        // Process accepted files
        if (acceptedFiles && acceptedFiles.length > 0) {
            processFiles(acceptedFiles);
        }
    }, [processFiles, isArabic]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragReject,
        open
    } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif']
        },
        multiple: true,
        disabled,
        noClick: true,
        noKeyboard: true,
        maxSize: maxSizeMB * 1024 * 1024,
    });

    // ✅ Handle retry
    const handleRetry = useCallback((tempId) => {
        retryUpload(tempId);
        showRetryMessage(isArabic);
    }, [retryUpload, isArabic]);

    // ✅ Delete uploaded image
    const handleDeleteImage = useCallback((image, index) => {
        if (typeof onChange === 'function') {
            onChange((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []));
        }
        showDeleteSuccess(isArabic);

        if (image.id) {
            deleteSingleMedia(image.id);
        }
    }, [onChange, isArabic, deleteSingleMedia]);

    return (
        <Box>
            {/* Label */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {isArabic ? 'صور المنتج' : 'Product Images'}
                {required && <span style={{ color: 'red' }}> *</span>}
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({value.length}/{maxImages})
                    {uploadQueue.length > 0 && ` - ${isArabic ? 'جاري الرفع' : 'Uploading'} (${uploadQueue.length})`}
                </Typography>
            </Typography>

            {/* Upload Drop Zone */}
            {canAddMoreImages(value.length, uploadQueue.length, maxImages) && (
                <UploadDropZone
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    isDragActive={isDragActive}
                    isDragReject={isDragReject}
                    isArabic={isArabic}
                    disabled={disabled}
                    maxImages={maxImages}
                    maxSizeMB={maxSizeMB}
                    isDark={isDark}
                    open={open}
                />
            )}

            {/* Images Grid */}
            <ImageGridDisplay
                value={value}
                uploadQueue={uploadQueue}
                uploadingImages={uploadingImages}
                errors={errors}
                isArabic={isArabic}
                isDark={isDark}
                onDeleteImage={handleDeleteImage}
                onRetry={handleRetry}
                onRemoveFromQueue={removeFromUploadQueue}
            />

            {/* Helper text */}
            {value.length === 0 && uploadQueue.length === 0 && required && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {isArabic ? 'مطلوب رفع صورة واحدة على الأقل' : 'At least one image is required'}
                </Typography>
            )}
        </Box>
    );
};

export default MultiImageUploader;
